import React, { useState, useEffect } from 'react';
import { 
  Users, ShieldAlert, Shield, Search, Trash2, 
  Activity, Terminal, AlertTriangle, RefreshCw, 
  Server, Wallet, Settings, Info, Save
} from 'lucide-react';
import api from '../api/api';

// Интерфейс пользователя
interface User {
  id: number;
  email: string;
  role: 'USER' | 'ADMIN';
  telegramChatId?: string;
  isBotActive: boolean;
  isActive: boolean;
  isPaperTrading: boolean;
  exchange: string;
  plan: string;
  paperBalance: number;
  subscriptionStatus: string;
  createdAt: string;
  hasApiKeys: boolean;
}

export const AdminPanel = () => {
  
  // State
  const [users, setUsers] = useState<User[]>([]);
  const [settings, setSettings] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'USERS' | 'CONFIG'>('USERS');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({ total: 0, activeBots: 0, realTraders: 0, revenue: 0 });

  useEffect(() => {
    fetchData();
  }, []);

  // ✅ ГЛАВНАЯ ФУНКЦИЯ ЗАГРУЗКИ
  const fetchData = async () => {
    setLoading(true);
    try {
      const [resUsers, resSettings] = await Promise.all([
          api.get('/users/admin/all').catch(() => ({ data: { users: [], stats: {} } })),
          api.get('/admin/scanner-settings').catch(() => ({ data: null }))
      ]);

      if (resUsers.data) {
          setUsers(resUsers.data.users || []);
          setStats(resUsers.data.stats || { total: 0, activeBots: 0, realTraders: 0, revenue: 0 });
      }
      if (resSettings.data) {
          setSettings(resSettings.data);
      }
    } catch (e) {
      console.error("Admin load error", e);
    } finally {
      setLoading(false);
    }
  };

  // --- ACTIONS: CONFIG ---
  const handleConfigChange = (key: string, value: string) => {
    // Авто-замена запятой на точку для чисел
    let cleanValue = value;
    if (key !== 'timeframe') {
       cleanValue = value.replace(',', '.');
    }
    setSettings({ ...settings, [key]: cleanValue });
  };

  const handleSaveConfig = async () => {
    if (!window.confirm("Применить новые настройки сканера?")) return;
    try {
      await api.post('/admin/scanner-settings', settings);
      alert('✅ Настройки сохранены! Сканер обновится через 1 минуту.');
      fetchData(); // Обновляем данные, чтобы убедиться, что сервер принял их
    } catch (e) { alert("Ошибка сохранения настроек"); }
  };

  // --- ACTIONS: USERS ---
  const handleToggleBan = async (id: number, currentStatus: boolean) => {
    if (!window.confirm(`Изменить статус бана для ID ${id}?`)) return;
    try {
      await api.post(`/users/admin/${id}/toggle-ban`, { isActive: !currentStatus });
      setUsers(users.map(u => u.id === id ? { ...u, isActive: !currentStatus } : u));
    } catch (e) { alert("Ошибка сервера"); }
  };

  const handleToggleBot = async (id: number, currentStatus: boolean) => {
    try {
      await api.post(`/trading/toggle`, { userId: id, status: !currentStatus }); 
      setUsers(users.map(u => u.id === id ? { ...u, isBotActive: !currentStatus } : u));
    } catch (e) { alert("Не удалось переключить бота"); }
  };

  const handlePanicSell = async (id: number) => {
    const confirm = window.confirm("🚨 PANIC SELL?\n\nЭто закроет ВСЕ позиции пользователя по рынку.");
    if (!confirm) return;
    try {
      await api.post(`/trading/panic-sell/${id}`);
      alert("Команда Panic Sell отправлена.");
    } catch (e) { alert("Ошибка Panic Sell"); }
  };

  const handleDeleteUser = async (id: number) => {
    if (!window.confirm("Удалить пользователя НАВСЕГДА?")) return;
    try {
      await api.delete(`/users/admin/${id}`);
      setUsers(users.filter(u => u.id !== id));
    } catch (e) { alert("Ошибка удаления"); }
  };

  const getPlanColor = (plan: string) => {
      switch(plan) {
          case 'INVESTOR': return 'bg-purple-500/20 text-purple-400 border-purple-500/50';
          case 'PRO': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50';
          case 'TRADER': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50';
          case 'START': return 'bg-blue-500/20 text-blue-400 border-blue-500/50';
          default: return 'bg-slate-700 text-slate-400 border-slate-600';
      }
  };

  const filteredUsers = users.filter(u => 
    u.email.toLowerCase().includes(searchTerm.toLowerCase()) || 
    (u.telegramChatId && u.telegramChatId.includes(searchTerm)) ||
    u.id.toString().includes(searchTerm)
  );

  return (
    <div className="min-h-screen bg-[#0f172a] text-slate-200 font-sans p-6">
      <div className="max-w-[1600px] mx-auto animate-in fade-in duration-500 pb-20">
        
        {/* HEADER & TABS */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
          <div>
            <h2 className="text-3xl font-bold text-white flex items-center gap-3">
              <ShieldAlert className="text-blue-500" size={32} /> Admin Panel
            </h2>
            <p className="text-slate-400 mt-1 text-sm">System Control Center</p>
          </div>

          {/* Tab Switcher */}
          <div className="flex bg-slate-800 p-1.5 rounded-xl border border-slate-700">
             <button 
                onClick={() => setActiveTab('USERS')}
                className={`px-6 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-all ${activeTab === 'USERS' ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}
             >
                <Users size={16} /> Users
             </button>
             <button 
                onClick={() => setActiveTab('CONFIG')}
                className={`px-6 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-all ${activeTab === 'CONFIG' ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}
             >
                <Settings size={16} /> Config
             </button>
          </div>
        </div>

        {/* ==========================
            TAB 1: SCANNER CONFIG
           ========================== */}
        {activeTab === 'CONFIG' && settings && (
            <div className="animate-in fade-in slide-in-from-bottom-4">
                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        
                        {/* General */}
                        <ConfigCard title="Фильтры Сканера (General)" icon={<Activity size={18} className="text-blue-400"/>}>
                            <ConfigInput 
                                label="Min Volume (USDT)" 
                                value={settings.minVolume} 
                                onChange={(v: string) => handleConfigChange('minVolume', v)} 
                                isInteger 
                                desc="Минимальный суточный объем торгов. Если меньше — монета считается 'мертвой' и игнорируется. (Рекомендуем: 1000000+)" 
                            />
                            <ConfigInput 
                                label="BB Width Threshold" 
                                value={settings.bbWidth} 
                                onChange={(v: string) => handleConfigChange('bbWidth', v)} 
                                desc="Фильтр волатильности полос Боллинджера. 0.02 = 2%. Если канал уже, чем это число — бот не торгует (флэт). Поставьте 0, чтобы отключить." 
                            />
                            <ConfigInput 
                                label="Cooldown (Min)" 
                                value={settings.cooldownMinutes} 
                                onChange={(v: string) => handleConfigChange('cooldownMinutes', v)} 
                                isInteger 
                                desc="Задержка в минутах перед повторным входом в ту же монету (защита от спама сделками)." 
                            />
                            <ConfigInput 
                                label="Timeframe" 
                                value={settings.timeframe} 
                                onChange={(v: string) => handleConfigChange('timeframe', v)} 
                                type="text" 
                                desc="Таймфрейм свечей для анализа (5m, 15m, 1h). Для скальпинга лучше 5m или 15m." 
                            />
                        </ConfigCard>

                        {/* RSI Logic */}
                        <ConfigCard title="Стратегия RSI (Scalp/Sniper)" icon={<RefreshCw size={18} className="text-purple-400"/>}>
                            <ConfigInput 
                                label="RSI Oversold (Buy)" 
                                value={settings.rsiOversold} 
                                onChange={(v: string) => handleConfigChange('rsiOversold', v)} 
                                desc="Уровень ПОКУПКИ для Scalp. Если RSI ниже этого числа (например 30), бот считает цену дешевой." 
                            />
                            <ConfigInput 
                                label="RSI Overbought (Sell)" 
                                value={settings.rsiOverbought} 
                                onChange={(v: string) => handleConfigChange('rsiOverbought', v)} 
                                desc="Уровень ПРОДАЖИ для Scalp. Если RSI выше этого числа (например 70), бот фиксирует прибыль или шортит." 
                            />
                            <ConfigInput 
                                label="Panic Oversold (Sniper)" 
                                value={settings.panicOversold} 
                                onChange={(v: string) => handleConfigChange('panicOversold', v)} 
                                desc="Экстремальная зона для стратегии SNIPER. Срабатывает только при сильных обвалах (например, RSI < 20)." 
                            />
                        </ConfigCard>

                        {/* Risk Normal */}
                        <ConfigCard title="Риск-менеджмент (Обычный рынок)" icon={<Info size={18} className="text-green-400"/>}>
                            <ConfigInput 
                                label="SL Multiplier (ATR)" 
                                value={settings.slMultNormal} 
                                onChange={(v: string) => handleConfigChange('slMultNormal', v)} 
                                desc="Множитель Стоп-Лосса (ATR). Чем больше число, тем дальше стоп от цены входа (например 1.8)." 
                            />
                            <ConfigInput 
                                label="TP Multiplier (ATR)" 
                                value={settings.tpMultNormal} 
                                onChange={(v: string) => handleConfigChange('tpMultNormal', v)} 
                                desc="Множитель Тейк-Профита (ATR). Чем больше число, тем больше прибыли бот пытается забрать (например 2.5)." 
                            />
                        </ConfigCard>

                        {/* Risk Panic */}
                        <ConfigCard title="Риск-менеджмент (Режим Паники)" icon={<AlertTriangle size={18} className="text-orange-400"/>}>
                            <ConfigInput 
                                label="FGI Panic Level" 
                                value={settings.fgiPanicLevel} 
                                onChange={(v: string) => handleConfigChange('fgiPanicLevel', v)} 
                                isInteger 
                                desc="Уровень Индекса Страха (0-100). Если рынок падает ниже этого числа (например 20), включается Panic Mode с расширенными стопами." 
                            />
                            <ConfigInput 
                                label="Panic SL Mult" 
                                value={settings.slMultPanic} 
                                onChange={(v: string) => handleConfigChange('slMultPanic', v)} 
                                desc="Увеличенный Стоп-Лосс для режима паники (чтобы не выбило случайной свечой)." 
                            />
                            <ConfigInput 
                                label="Panic TP Mult" 
                                value={settings.tpMultPanic} 
                                onChange={(v: string) => handleConfigChange('tpMultPanic', v)} 
                                desc="Увеличенный Тейк-Профит для режима паники (ловим сильный отскок)." 
                            />
                        </ConfigCard>
                    </div>

                    <div className="flex justify-end pt-4">
                        <button onClick={handleSaveConfig} className="bg-green-600 hover:bg-green-500 text-white px-8 py-4 rounded-xl font-bold flex items-center gap-3 shadow-lg hover:shadow-green-500/20 transition-all text-lg">
                            <Save size={24}/> Сохранить настройки
                        </button>
                    </div>
                </div>
            </div>
        )}

        {/* ==========================
            TAB 2: USER MANAGEMENT
           ========================== */}
        {activeTab === 'USERS' && (
            <div className="animate-in fade-in slide-in-from-bottom-4">
                
                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <StatCard label="Total Users" value={stats.total} icon={<Users size={24} className="text-slate-500"/>} />
                    <StatCard label="Active Bots" value={stats.activeBots} icon={<Activity size={24} className="text-green-500 animate-pulse"/>} color="text-green-400" bg="bg-green-900/10 border-green-500/20" />
                    <StatCard label="Real Traders" value={stats.realTraders} icon={<Shield size={24} className="text-blue-500"/>} color="text-blue-400" bg="bg-blue-900/10 border-blue-500/20" />
                    <StatCard label="Revenue" value={`$${stats.revenue}`} icon={<Wallet size={24} className="text-yellow-500"/>} color="text-yellow-400" bg="bg-yellow-900/10 border-yellow-500/20" />
                </div>

                {/* Toolbar */}
                <div className="flex flex-col md:flex-row justify-between items-center mb-6 bg-[#1e293b] p-4 rounded-2xl border border-slate-800 gap-4">
                    <div className="relative w-full max-w-md">
                        <Search className="absolute left-3 top-3 text-slate-500" size={18} />
                        <input 
                            type="text" 
                            placeholder="Поиск по email, ID..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="bg-slate-900 border border-slate-700 text-white rounded-xl py-2 pl-10 pr-4 focus:outline-none focus:border-blue-500 w-full transition-all font-mono text-sm"
                        />
                    </div>
                    <button onClick={fetchData} className="p-2 bg-slate-800 rounded-xl text-slate-400 hover:text-white border border-slate-700 hover:bg-slate-700 transition-all">
                        <RefreshCw size={20} />
                    </button>
                </div>

                {/* Table */}
                <div className="bg-[#1e293b] border border-slate-800 rounded-3xl overflow-hidden shadow-xl">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-slate-950 text-[10px] uppercase text-slate-500 font-bold tracking-wider">
                                <tr>
                                    <th className="p-5">User Profile</th>
                                    <th className="p-5">Plan</th>
                                    <th className="p-5">Exchange</th>
                                    <th className="p-5 text-center">Bot State</th>
                                    <th className="p-5 text-center">Access</th>
                                    <th className="p-5 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-800/50">
                                {loading ? (
                                    <tr><td colSpan={6} className="p-12 text-center text-slate-500 animate-pulse">Загрузка данных...</td></tr>
                                ) : filteredUsers.map(user => (
                                    <tr key={user.id} className="hover:bg-slate-800/40 transition-colors group">
                                        <td className="p-5">
                                            <div className="flex items-center gap-4">
                                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold text-white shadow-lg ${user.role === 'ADMIN' ? 'bg-red-600' : 'bg-gradient-to-br from-blue-600 to-indigo-600'}`}>
                                                    {user.role === 'ADMIN' ? <Shield size={18}/> : user.email.charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <div className="font-bold text-white text-sm flex items-center gap-2">
                                                        {user.email}
                                                        {user.telegramChatId && <Terminal size={12} className="text-blue-400" />}
                                                    </div>
                                                    <div className="text-[10px] text-slate-500 font-mono mt-0.5">ID: {user.id} • {new Date(user.createdAt).toLocaleDateString()}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-5">
                                            <span className={`text-[10px] font-bold px-2 py-1 rounded border ${getPlanColor(user.plan || 'FREE')}`}>
                                                {user.plan || 'FREE'}
                                            </span>
                                        </td>
                                        <td className="p-5">
                                            <div className="flex flex-col gap-1">
                                                <span className={`text-xs font-bold uppercase flex items-center gap-1.5 ${user.exchange === 'bybit' ? 'text-white' : 'text-[#FCD535]'}`}>
                                                    <Server size={12}/> {user.exchange || 'BINANCE'}
                                                </span>
                                                <span className="text-[10px] text-slate-500 font-mono">
                                                    {user.isPaperTrading ? <span className='text-orange-400'>PAPER: ${user.paperBalance?.toFixed(0)}</span> : <span className='text-green-400'>REAL</span>}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="p-5 text-center">
                                            <button onClick={() => handleToggleBot(user.id, user.isBotActive)} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${user.isBotActive ? 'bg-green-500' : 'bg-slate-700'}`}>
                                                <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${user.isBotActive ? 'translate-x-5' : 'translate-x-1'}`} />
                                            </button>
                                        </td>
                                        <td className="p-5 text-center">
                                            <button onClick={() => handleToggleBan(user.id, user.isActive)} className={`text-[10px] font-bold uppercase tracking-wider transition-all hover:underline ${user.isActive ? 'text-slate-500 hover:text-red-400' : 'text-red-500'}`}>
                                                {user.isActive ? 'Active' : '🚫 BANNED'}
                                            </button>
                                        </td>
                                        <td className="p-5 text-right">
                                            <div className="flex items-center justify-end gap-2 opacity-50 group-hover:opacity-100 transition-opacity">
                                                <button onClick={() => handlePanicSell(user.id)} className="p-2 bg-orange-500/10 text-orange-500 border border-orange-500/20 hover:bg-orange-500 hover:text-white rounded-lg transition-all" title="Panic Sell"><AlertTriangle size={14} /></button>
                                                <button onClick={() => handleDeleteUser(user.id)} className="p-2 bg-red-500/10 text-red-500 border border-red-500/20 hover:bg-red-500 hover:text-white rounded-lg transition-all" title="Delete"><Trash2 size={14} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        )}
      </div>
    </div>
  );
};

// --- HELPER COMPONENTS ---

interface StatCardProps {
    label: string;
    value: string | number;
    icon: React.ReactNode;
    color?: string;
    bg?: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon, color="text-white", bg="bg-slate-800/50 border-slate-700" }) => (
    <div className={`${bg} p-4 rounded-2xl border flex items-center justify-between`}>
        <div>
            <p className={`text-[10px] uppercase font-bold tracking-widest ${color.replace('text-', 'text-opacity-70 ')}`}>{label}</p>
            <p className={`text-2xl font-black ${color}`}>{value}</p>
        </div>
        {icon}
    </div>
);

interface ConfigCardProps {
    title: string;
    icon: React.ReactNode;
    children: React.ReactNode;
}

const ConfigCard: React.FC<ConfigCardProps> = ({ title, icon, children }) => (
    <div className="bg-[#1e293b] p-6 rounded-2xl border border-slate-700/50 shadow-lg hover:border-slate-600 transition-colors h-full">
        <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2 border-b border-slate-700/50 pb-3">
            {icon} {title}
        </h3>
        <div className="space-y-5">
            {children}
        </div>
    </div>
);

interface ConfigInputProps {
    label: string;
    desc?: string;
    value: string | number;
    onChange: (val: string) => void;
    type?: string;
    isInteger?: boolean; // Новый проп: только целые числа
}

// 🔥 ФИНАЛЬНЫЙ КОМПОНЕНТ: Поддерживает и целые, и дробные числа (через точку или запятую)
const ConfigInput: React.FC<ConfigInputProps> = ({ label, desc, value, onChange, type="text", isInteger = false }) => (
    <div>
        <div className="flex justify-between mb-1.5">
            <label className="text-sm font-bold text-slate-300 tracking-wide">{label}</label>
            <div className="group relative">
                <Info size={14} className="text-slate-500 cursor-help hover:text-blue-400 transition-colors"/>
                <div className="absolute right-0 bottom-full mb-2 w-64 p-3 bg-slate-950 border border-slate-800 text-xs text-slate-300 rounded-xl shadow-2xl hidden group-hover:block z-50 pointer-events-none leading-relaxed">
                    {desc}
                    <div className="absolute bottom-[-6px] right-3 w-3 h-3 bg-slate-950 border-r border-b border-slate-800 transform rotate-45"></div>
                </div>
            </div>
        </div>
        <input 
            type="text" 
            inputMode={isInteger ? "numeric" : "decimal"}
            value={value || ''} 
            onChange={e => {
                let val = e.target.value;
                if (type === 'text' && !isInteger) { // Для Timeframe (5m, 1h)
                    onChange(val);
                    return;
                }
                
                // Для чисел
                val = val.replace(',', '.');
                const regex = isInteger ? /^[0-9]*$/ : /^[0-9]*\.?[0-9]*$/;
                
                if (regex.test(val)) {
                    onChange(val);
                }
            }}
            className="w-full bg-[#0f172a] border border-slate-700 rounded-xl px-4 py-3 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none font-mono transition-all shadow-inner"
        />
    </div>
);